import { useEffect, useRef, useState } from "react";
import { FloatingPanel } from "./FloatingPanel";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Secondary text rendered under the label (product/version, date/state...). */
  description?: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Type-to-filter text input (default). `false` renders a button-based, select-only combobox. */
  searchable?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  emptyLabel?: string;
  /** More options exist on the server: shows a "Cargar más" action at the end of the list. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  loadMoreLabel?: string;
  required?: boolean;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

function normalize(text: string): string {
  return text.trim().toLowerCase();
}

function matches(option: ComboboxOption, filter: string): boolean {
  return normalize(`${option.label} ${option.description ?? ""}`).includes(filter);
}

function firstEnabled(options: readonly ComboboxOption[]): number {
  return options.findIndex((option) => !option.disabled);
}

function lastEnabled(options: readonly ComboboxOption[]): number {
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (!options[index]!.disabled) return index;
  }
  return -1;
}

function step(options: readonly ComboboxOption[], from: number, direction: 1 | -1): number {
  for (let index = from + direction; index >= 0 && index < options.length; index += direction) {
    if (!options[index]!.disabled) return index;
  }
  return from;
}

/**
 * Reusable single-select combobox following the WAI-ARIA 1.2 combobox/listbox
 * pattern (panel feedback 2026-10-30, T4). `searchable` (default) is an
 * editable input that filters options as the user types; `searchable={false}`
 * is a select-only button for short fixed lists. The listbox is a
 * `FloatingPanel` (portal + fixed positioning), so it never gets clipped by a
 * modal's scroll container and always matches the trigger's width.
 *
 * Keyboard: ArrowDown/ArrowUp move the active option (disabled options are
 * skipped), Home/End jump to the first/last, Enter selects, Escape closes
 * (without bubbling to a surrounding Modal), Tab closes. Focus always stays
 * on the trigger; the active option is exposed via `aria-activedescendant`.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  searchable = true,
  loading,
  loadingLabel = "Cargando…",
  emptyLabel = "Sin resultados",
  hasMore,
  loadingMore,
  onLoadMore,
  loadMoreLabel = "Cargar más",
  required,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: ComboboxProps) {
  const listboxId = `${id}-listbox`;
  const optionId = (index: number) => `${id}-option-${index}`;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  /** `null` = the user has not typed since opening: every option is shown. */
  const [query, setQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const selected = options.find((option) => option.value === value);
  const filter = normalize(query ?? "");
  const filtered = filter ? options.filter((option) => matches(option, filter)) : options;

  function close() {
    setOpen(false);
    setQuery(null);
    setActiveIndex(-1);
  }

  function openList(initialActive?: number) {
    if (disabled) return;
    if (!open) {
      setOpen(true);
      setQuery(null);
      setActiveIndex(
        initialActive ?? options.findIndex((option) => option.value === value && !option.disabled),
      );
    }
  }

  function select(option: ComboboxOption) {
    if (option.disabled) return;
    onChange(option.value);
    close();
  }

  // Outside click closes (Safari does not focus a button on click, so blur
  // alone cannot be relied on).
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
      setQuery(null);
      setActiveIndex(-1);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(`${id}-option-${activeIndex}`)?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, id]);

  function handleKeyDown(event: React.KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        if (!open) {
          openList(event.key === "ArrowDown" ? undefined : lastEnabled(options));
          if (event.key === "ArrowDown") {
            const selectedIndex = options.findIndex((option) => option.value === value && !option.disabled);
            setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstEnabled(options));
          }
          return;
        }
        setActiveIndex((current) => {
          if (current < 0) return event.key === "ArrowDown" ? firstEnabled(filtered) : lastEnabled(filtered);
          return step(filtered, current, event.key === "ArrowDown" ? 1 : -1);
        });
        return;
      }
      case "Home":
        if (open) {
          event.preventDefault();
          setActiveIndex(firstEnabled(filtered));
        }
        return;
      case "End":
        if (open) {
          event.preventDefault();
          setActiveIndex(lastEnabled(filtered));
        }
        return;
      case "Enter": {
        if (!open) {
          if (!searchable) {
            event.preventDefault();
            openList();
          }
          return;
        }
        event.preventDefault();
        const option = filtered[activeIndex];
        if (option) select(option);
        return;
      }
      case " ":
        if (!searchable) {
          event.preventDefault();
          const option = open ? filtered[activeIndex] : undefined;
          if (option) select(option);
          else if (!open) openList();
        }
        return;
      case "Escape":
        if (open) {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
        return;
      case "Tab":
        if (open) close();
        return;
      default:
    }
  }

  const sharedProps = {
    id,
    role: "combobox" as const,
    "aria-expanded": open,
    "aria-controls": listboxId,
    "aria-autocomplete": "list" as const,
    "aria-haspopup": "listbox" as const,
    "aria-activedescendant": open && activeIndex >= 0 ? optionId(activeIndex) : undefined,
    "aria-required": required || undefined,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    disabled,
    onKeyDown: handleKeyDown,
    onBlur: close,
  };

  const classes = ["v3-combobox", className, disabled ? "v3-combobox--disabled" : ""].filter(Boolean).join(" ");

  return (
    <div className={classes} ref={wrapperRef}>
      {searchable ? (
        <input
          {...sharedProps}
          type="text"
          className="v3-combobox__control"
          autoComplete="off"
          // While open the field is a fresh search box (the current label
          // moves to the placeholder so typing never appends to it).
          value={open ? (query ?? "") : (selected?.label ?? "")}
          placeholder={open && selected ? selected.label : placeholder}
          onFocus={() => openList()}
          onClick={() => openList()}
          onChange={(event) => {
            const next = event.target.value;
            setOpen(true);
            setQuery(next);
            const nextFilter = normalize(next);
            setActiveIndex(
              firstEnabled(nextFilter ? options.filter((option) => matches(option, nextFilter)) : options),
            );
          }}
        />
      ) : (
        <button
          {...sharedProps}
          type="button"
          className="v3-combobox__control v3-combobox__control--button"
          onClick={() => (open ? close() : openList())}
        >
          <span className={selected ? "v3-combobox__value" : "v3-combobox__value v3-combobox__value--placeholder"}>
            {selected?.label ?? placeholder ?? ""}
          </span>
        </button>
      )}
      <svg
        className="v3-combobox__chevron"
        viewBox="0 0 20 20"
        width="16"
        height="16"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M5 7.5l5 5 5-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <FloatingPanel anchorRef={wrapperRef} panelRef={panelRef} open={open}>
        <ul id={listboxId} role="listbox" className="v3-combobox__listbox" aria-labelledby={ariaLabelledBy}>
          {loading ? (
            <li className="v3-combobox__status" role="presentation">
              {loadingLabel}
            </li>
          ) : filtered.length === 0 ? (
            <li className="v3-combobox__status" role="presentation">
              {emptyLabel}
            </li>
          ) : (
            filtered.map((option, index) => (
              <li
                key={option.value}
                id={optionId(index)}
                data-value={option.value}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined}
                className={[
                  "v3-combobox__option",
                  index === activeIndex ? "v3-combobox__option--active" : "",
                  option.value === value ? "v3-combobox__option--selected" : "",
                  option.disabled ? "v3-combobox__option--disabled" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onMouseMove={() => {
                  if (!option.disabled && index !== activeIndex) setActiveIndex(index);
                }}
                onClick={() => select(option)}
              >
                <span className="v3-combobox__option-label">{option.label}</span>
                {option.description && <span className="v3-combobox__option-description">{option.description}</span>}
              </li>
            ))
          )}
        </ul>
        {hasMore && onLoadMore && (
          <button type="button" className="v3-combobox__load-more" disabled={loadingMore} onClick={onLoadMore}>
            {loadingMore ? loadingLabel : loadMoreLabel}
          </button>
        )}
      </FloatingPanel>
    </div>
  );
}
